/**
 * Shared error card used by every admin view. Goal: upgrade every error
 * from "Error: Request failed: 401" into a problem/cause/fix card that
 * tells the operator what's wrong, why, and what to do next — the
 * Elm/Rust/Stripe three-tier model.
 *
 * - 401 / 403 → "You're not signed in as an admin" + Sign Out escape
 * - 5xx      → "The server is having trouble" + Retry
 * - network  → "Can't reach Supabase" + Retry
 * - other    → echo the raw message + Retry
 */
import { Button } from '@perch/ui'

export interface InterpretedError {
  title: string
  detail: string
  cause?: string
  action: 'retry' | 'sign-out' | 'none'
}

/** Pulls a status code out of a message like "Request failed: 401". */
function extractStatus(msg: string): number | null {
  const m = msg.match(/\b([45]\d{2})\b/)
  return m ? parseInt(m[1], 10) : null
}

export function interpretError(raw: string): InterpretedError {
  const status = extractStatus(raw)
  const lower = raw.toLowerCase()

  if (status === 401) {
    return {
      title: 'Your session expired',
      detail: 'Sign out and sign back in with your admin Google account.',
      cause: raw,
      action: 'sign-out',
    }
  }
  if (status === 403) {
    return {
      title: 'Not an admin account',
      detail:
        'Your email is not on the ADMIN_EMAILS allowlist. Update the supabase secret (ADMIN_EMAILS) and sign in again.',
      cause: raw,
      action: 'sign-out',
    }
  }
  if (status && status >= 500) {
    return {
      title: 'The server is having trouble',
      detail:
        'The edge function returned a 5xx. Check Ops Health for the failing probe, then retry.',
      cause: raw,
      action: 'retry',
    }
  }
  if (lower.includes('failed to fetch') || lower.includes('networkerror')) {
    return {
      title: "Can't reach Supabase",
      detail: 'Check your internet, VPN, or that the Supabase project is up, then retry.',
      cause: raw,
      action: 'retry',
    }
  }
  return {
    title: 'Something went wrong',
    detail: raw,
    action: 'retry',
  }
}

export function ErrorState({
  error,
  onRetry,
  onSignOut,
  className = '',
}: {
  error: string
  onRetry?: () => void
  onSignOut?: () => void
  className?: string
}) {
  const info = interpretError(error)
  const showRetry = info.action === 'retry' && !!onRetry
  const showSignOut = info.action === 'sign-out' && !!onSignOut

  return (
    <div
      className={`bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-6 ${className}`}
    >
      <p className="text-red-700 dark:text-red-400 font-medium">{info.title}</p>
      <p className="mt-1 text-sm text-red-600/90 dark:text-red-400/90">{info.detail}</p>
      {info.cause && info.cause !== info.detail && (
        <p className="mt-2 font-mono text-xs text-red-500/70 dark:text-red-400/60">
          {info.cause}
        </p>
      )}
      {(showRetry || showSignOut) && (
        <div className="mt-4 flex gap-2">
          {showRetry && (
            <Button onClick={onRetry} variant="danger" size="sm">
              Retry
            </Button>
          )}
          {showSignOut && (
            <Button onClick={onSignOut} variant="danger" size="sm">
              Sign Out
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
